import { IS_PROD } from "@/constants/app-defaults";
import { DEFAULT_SEO, SEO_TEMPLATES } from "@/constants/seo";
import { APP_BASE_URL } from "@/constants/app-defaults";

/**
 * SEO metadata and text security sanitization utility
 */

/**
 * Sanitizes raw text fetched from the backend.
 * - Strips any HTML tags to prevent XSS and layout breakout in meta tags and attributes.
 * - Removes control characters, newlines, and carriage returns.
 * - Normalizes multiple spaces into a single space.
 */
export const sanitizeText = (text: string | undefined | null): string => {
  if (!text) return "";

  // 1. Strip HTML tags
  let cleaned = text.replace(/<\/?[^>]+(>|$)/g, "");

  // 2. Remove control characters and convert newlines/carriage returns to spaces
  cleaned = cleaned.replace(/[\r\n\x00-\x1F\x7F]+/g, " ");

  // 3. Collapse consecutive whitespace characters into a single space
  cleaned = cleaned.replace(/\s+/g, " ");

  return cleaned.trim();
};

/**
 * Generates canonical URLs following sheet 5 rules:
 * - Keeps collection id or category_id if present
 * - Keeps product_subtype_id if present
 */
export const generateCanonicalUrl = (
  slug: string,
  searchParams?: { [key: string]: string | string[] | undefined },
): string => {
  const searchQueryList: string[] = [];

  if (searchParams) {
    // 1. Check for collection/category ID
    const rawCollectionId = searchParams.id || searchParams.collection_id;
    const collectionId = Array.isArray(rawCollectionId)
      ? rawCollectionId[0]
      : rawCollectionId;

    const rawCategoryId = searchParams.category_id;
    const categoryId = Array.isArray(rawCategoryId)
      ? rawCategoryId[0]
      : rawCategoryId;

    if (collectionId) {
      // If collection_id or id is in searchParams, check if the source was category_id
      if (searchParams.category_id) {
        searchQueryList.push(`category_id=${collectionId}`);
      } else {
        searchQueryList.push(`id=${collectionId}`);
      }
    } else if (categoryId) {
      searchQueryList.push(`category_id=${categoryId}`);
    }

    // 2. Check for product subtype ID
    const rawSubtypeId = searchParams.product_subtype_id;
    const subtypeId = Array.isArray(rawSubtypeId)
      ? rawSubtypeId[0]
      : rawSubtypeId;
    if (subtypeId) {
      searchQueryList.push(`product_subtype_id=${subtypeId}`);
    }
  }

  const searchQuery = searchQueryList.join("&");
  const baseUrl = APP_BASE_URL;

  return `${baseUrl}/listing/${slug}${searchQuery ? `?${searchQuery}` : ""}`;
};

/**
 * Generates dynamic alt text for images using vendor business name.
 */
export const generateProductAltText = (
  productName: string,
  vendorName?: string,
): string => {
  return sanitizeText(SEO_TEMPLATES.productImageAlt(productName, vendorName));
};

/**
 * Optimizes an image URL specifically for OG and Twitter cards using Next.js automatic image optimization endpoint.
 * This compresses high-res product/baker images to standard, fast-loading 1200px width with 75% quality.
 */
export const optimizeOgImageUrl = (url: string | undefined | null): string => {
  if (!url) return "";

  // Only route through Next.js optimizer if it's a relative path or an allowed domain to avoid 400 Bad Request
  if (
    url.startsWith("/") ||
    url.includes("toofi.s3.amazonaws.com") ||
    url.includes("d172l6dcz7gvv1.cloudfront.net")
  ) {
    const absoluteBase = APP_BASE_URL;
    const cleanUrl = url.startsWith("http")
      ? url
      : `${absoluteBase}${url.startsWith("/") ? "" : "/"}${url}`;
    return `${absoluteBase}/_next/image?url=${encodeURIComponent(cleanUrl)}&w=1200&q=75`;
  }

  return url;
};

/**
 * Central metadata factory to eliminate duplicate boilerplate code across Next.js app routes.
 */
export const generateBaseMetadata = ({
  title,
  description,
  canonicalUrl,
  imageUrl,
}: {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  imageUrl?: string;
}) => {
  const seoTitle = sanitizeText(title || DEFAULT_SEO.TITLE);
  const seoDescription = sanitizeText(description || DEFAULT_SEO.DESCRIPTION);
  const seoCanonical = canonicalUrl || DEFAULT_SEO.URL;
  const seoImage = optimizeOgImageUrl(imageUrl || DEFAULT_SEO.IMAGE);

  const meta: Record<string, string | number | boolean | Record<string, unknown> | Array<unknown>> = {
    title: seoTitle,
    description: seoDescription,
    alternates: {
      canonical: seoCanonical,
    },
    openGraph: {
      title: seoTitle,
      description: seoDescription,
      url: seoCanonical,
      images: seoImage ? [{ url: seoImage }] : [],
    },
    twitter: {
      title: seoTitle,
      description: seoDescription,
      images: seoImage ? [seoImage] : [],
    },
  };

  if (IS_PROD) {
    meta.robots = {
      index: true,
      follow: true,
    };
  } else {
    meta.robots = {
      index: false,
      follow: false,
    };
  }

  return meta;
};
